/**
 * DescriptionInput
 *
 * Textarea with # and @ overlay buttons for the description step.
 * Matches Flutter's _editContentWidget + _contentDecorationsButtons.
 */

import React from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { InputLengthFormatters } from '@/presentation/constants/formatters/inputLengthFormatters';
import { InputText } from '@/presentation/components/common/InputText';

interface DescriptionInputProps {
  value: string;
  inputRef: React.MutableRefObject<HTMLTextAreaElement | null>;
  isEditingMentions: boolean;
  onChange: (newValue: string, cursorPos: number, setValue: (text: string, cursorPos: number) => void) => void;
  onFocusChange: (focused: boolean) => void;
  onInsertHashtag: () => void;
  onInsertMention: () => void;
  setValueAndCursor: (text: string, cursorPos: number) => void;
}

const MAX_DESCRIPTION_LENGTH = InputLengthFormatters.contentDescriptionMaxLength;

export const DescriptionInput: React.FC<DescriptionInputProps> = ({
  value,
  inputRef,
  isEditingMentions,
  onChange,
  onFocusChange,
  onInsertHashtag,
  onInsertMention,
  setValueAndCursor,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const { l10n } = useLocale();

  return (
    <div
      style={{
        position: 'relative',
        backgroundColor: theme.secondary,
        borderRadius: responsive.borderRadius.base,
        paddingLeft: responsive.spacing.lg,
        paddingRight: responsive.spacing.lg,
        paddingBottom: responsive.spacing['3xl'],
      }}
    >
      <InputText
        qa={{ id: 'add-content-description' }}
        inputRef={inputRef}
        value={value}
        maxLength={MAX_DESCRIPTION_LENGTH}
        placeholder={l10n.publish.descriptionPlaceholder}
        disabled={isEditingMentions}
        onChange={(next) => {
          const cursor = inputRef.current?.selectionStart ?? next.length;
          onChange(next, cursor, setValueAndCursor);
        }}
        onFocus={() => onFocusChange(true)}
        onBlur={() => onFocusChange(false)}
        backgroundColor="transparent"
        height={responsive.form.textareaHeight}
        lineHeight={1.4}
        style={{
          width: '100%',
          paddingTop: responsive.spacing.sm,
          paddingRight: 0,
          paddingBottom: 0,
          paddingLeft: 0,
        }}
      />

      {/* Decoration buttons - bottom right */}
      <div
        style={{
          position: 'absolute',
          bottom: responsive.spacing.sm,
          right: responsive.spacing.sm,
          display: 'flex',
          gap: responsive.spacing.sm,
        }}
      >
        <button
          onClick={onInsertHashtag}
          disabled={isEditingMentions}
          style={{
            width: responsive.button.size.small,
            height: responsive.button.size.small,
            borderRadius: '50%',
            backgroundColor: theme.primary,
            border: 'none',
            cursor: isEditingMentions ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
          }}
        >
          <AssetIcon
            path="/assets/images/hashtag.png"
            width={responsive.button.size.xxs}
            height={responsive.button.size.xxs}
            color={theme.lightBlue}
          />
        </button>

        <button
          onClick={onInsertMention}
          disabled={isEditingMentions}
          style={{
            width: responsive.button.size.small,
            height: responsive.button.size.small,
            borderRadius: '50%',
            backgroundColor: theme.primary,
            border: 'none',
            cursor: isEditingMentions ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
          }}
        >
          <AssetIcon
            path="/assets/images/mention.png"
            width={responsive.button.size.xxs}
            height={responsive.button.size.xxs}
            color={theme.exclusive}
          />
        </button>
      </div>
    </div>
  );
};
